import { MetricInfoTrigger } from '@/components/data/MetricInfo';
import { Badge } from '@/components/ui/badge';
import { formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import type { Verdict } from '@/lib/metric-info';
import { cn } from '@/lib/utils';

export type CellFormat = 'pct' | 'ratio' | 'nok' | 'seconds';

const STATUS_TEXT: Record<Verdict, string> = {
  good: 'text-good-fg',
  warn: 'text-warn-fg',
  bad: 'text-bad-fg',
};

const MESSAGES = {
  nb: { good: 'Sterk', warn: 'Ok', bad: 'Svak', videoOnly: 'kun video' },
  en: { good: 'Strong', warn: 'OK', bad: 'Weak', videoOnly: 'video only' },
};

const EM_DASH = '—';

function format(value: number | null, fmt: CellFormat): string {
  if (value == null || Number.isNaN(value)) return EM_DASH;
  if (fmt === 'seconds') return `${formatMetric(value, 'ratio').replace('×', '')} s`;
  return formatMetric(value, fmt);
}

/**
 * One engagement-diagnostic cell: a metric name (hover card with benchmark +
 * advice), its value tinted by the benchmark verdict, and a Sterk/Ok/Svak badge.
 * `verdict` is null for value-only metrics (cost-per) or when there's no value.
 * `unavailable` renders the "kun video" placeholder for image ads.
 */
export function EngagementMetricCell({
  metricKey,
  label,
  value,
  format: fmt,
  verdict,
  unavailable,
}: {
  metricKey: string;
  label: string;
  value: number | null;
  format: CellFormat;
  verdict: Verdict | null;
  unavailable?: boolean;
}) {
  const m = useViewMessages(MESSAGES);
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-surface px-2.5 py-2">
      <span className="text-micro text-fg-muted">
        <MetricInfoTrigger metricKey={metricKey} label={label} />
      </span>
      {unavailable ? (
        <span className="text-body tabular text-fg-subtle">
          {EM_DASH} <span className="text-micro">{m.videoOnly}</span>
        </span>
      ) : (
        <span
          className={cn(
            'text-body tabular font-medium',
            verdict ? STATUS_TEXT[verdict] : 'text-fg',
          )}
        >
          {format(value, fmt)}
        </span>
      )}
      {!unavailable && verdict ? <Badge tone={verdict}>{m[verdict]}</Badge> : null}
    </div>
  );
}
